import { deriveNextCardStateFromInteraction } from '@apps/admin/routes/DashboardPreview/DashboardPreview.utils';
import { OnCardInteract } from '@apps/suggestions/components/SuggestionCard';
import { DateFragment, EventSuggestionFragment, UpdateEventSuggestionMutationFn } from '@graphql/generated';

export const REGISTRY_SUGGESTION_IDENTIFIERS = [
  'onbrd-reg-add-shipping-address',
  'onbrd-reg-set-currency',
  'reg-shop',
  'reg-browse-cash-funds',
  'reg-connect-registries',
  'onbrd-reg-install-browser-button',
  'onbrd-reg-add-external-gift',
  'onboarding-invite-admin',
  'onbrd-reg-download-app',
  'onbrd-reg-add-registry-photo',
  'onbrd-reg-write-page-greeting',
  'onbrd-reg-share-registry'
] as const;

export type ActiveRegistrySuggestionIdentifiers = typeof REGISTRY_SUGGESTION_IDENTIFIERS[number];

export const REGISTRY_SUGGESTIONS_US: ActiveRegistrySuggestionIdentifiers[] = [
  'onbrd-reg-add-shipping-address',
  'onbrd-reg-set-currency',
  'reg-shop',
  'reg-browse-cash-funds',
  'onbrd-reg-install-browser-button',
  'onboarding-invite-admin',
  'onbrd-reg-download-app',
  'onbrd-reg-add-registry-photo',
  'onbrd-reg-write-page-greeting',
  'onbrd-reg-share-registry'
];

export const REGISTRY_SUGGESTIONS_GLOBAL: ActiveRegistrySuggestionIdentifiers[] = [
  'onbrd-reg-add-shipping-address',
  'onbrd-reg-set-currency',
  'reg-browse-cash-funds',
  'reg-connect-registries',
  'onbrd-reg-install-browser-button',
  'onbrd-reg-add-external-gift',
  'onboarding-invite-admin',
  'onbrd-reg-download-app',
  'onbrd-reg-add-registry-photo',
  'onbrd-reg-write-page-greeting',
  'onbrd-reg-share-registry'
];

export const findSuggestion = (identifier: ActiveRegistrySuggestionIdentifiers, suggestions?: EventSuggestionFragment[]) =>
  suggestions?.find(suggestion => suggestion.identifier === identifier);

export const isDone = (suggestion: EventSuggestionFragment) => !!suggestion.stateJSON?.usedAt || !!suggestion.stateJSON?.declinedAt;

export const doSuggestionCardInteract = (updateEventSuggestionMutation: UpdateEventSuggestionMutationFn) => (args: Parameters<OnCardInteract>[0]) => {
  const { id, identifier } = args;

  const nextStateJson = deriveNextCardStateFromInteraction(args);
  updateEventSuggestionMutation({
    variables: { id, payload: { stateJSON: nextStateJson } },
    optimisticResponse: {
      updateEventSuggestion: {
        __typename: 'EventSuggestion',
        // just dummy data till the response comes back
        createdAt: dummyDate,
        offeredAt: dummyDate,
        // actual optimistic data till the response comes back
        identifier,
        id,
        stateJSON: nextStateJson
      }
    }
  });
};

export const doSuggestionCardReset = (updateEventSuggestionMutation: UpdateEventSuggestionMutationFn, suggestions: EventSuggestionFragment[]) => (identifier: string) => {
  const suggestion = suggestions.find(suggestion => suggestion.identifier === identifier);

  if (!suggestion) return;

  const { id } = suggestion;

  updateEventSuggestionMutation({
    variables: { id: id, payload: { stateJSON: {} } },
    optimisticResponse: {
      updateEventSuggestion: {
        __typename: 'EventSuggestion',
        // just dummy data till the response comes back
        createdAt: dummyDate,
        offeredAt: dummyDate,
        // actual optimistic data till the response comes back
        identifier,
        id,
        stateJSON: {}
      }
    }
  });
};

// Better optimistical values to enable sortability, if ever necessary.
const now = new Date().getTime();

const dummyDate: DateFragment = {
  __typename: 'Date',
  timestamp: Math.floor(now / 1000),
  milliseconds: now % 1000,
  timezone: 'pst'
};
